/**
 * Classifies a `claimScenarioOwnership` rejection on the SE-8340 contract
 * (SE-8344, PV12 / R05-B).
 *
 * Both conflicts arrive under the pre-existing `INTERNAL_SERVER_ERROR`
 * envelope: the stale-revision refusal is a `GraphQLError` whose extension
 * code is `SCENARIO_REVISION_CONFLICT`, the already-owned refusal is a Nest
 * `ConflictException` whose MESSAGE is `ERR_SCENARIO_ALREADY_OWNED`. So the
 * first is matched on the code and the second on the message — the same
 * split the watchlist CAS hooks make between a conflict code and a
 * name-taken message. Anything else surfaces the backend's own wording.
 * @module features/squad-planning/utils/classifyClaimOwnershipError
 */
import {
  collectGraphQLErrors,
  type GraphQLErrorLike,
} from "./collectGraphQLErrors";
import { isScenarioRevisionConflict } from "./scenarioRevisionConflict";

/** Copy for a claim refused because the scenario changed under the user. */
export const REVISION_CONFLICT_MESSAGE =
  "The scenario changed · Reload and try again";

/** Copy for a claim refused because an owner already exists. */
export const ALREADY_CLAIMED_MESSAGE = "Already claimed";

/** Fallback when the rejection carries no readable message at all. */
export const CLAIM_FAILED_MESSAGE = "Could not claim ownership";

const ALREADY_OWNED_MESSAGE = "ERR_SCENARIO_ALREADY_OWNED";
const FORBIDDEN_CODE = "FORBIDDEN";

/** How a claim was refused, and the copy to show for it. */
export interface ClaimOwnershipRejection {
  readonly kind: "revision-conflict" | "already-owned" | "forbidden" | "other";
  readonly message: string;
}

const textOf = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

/**
 * Every message a GraphQL error can carry, top-level and nested.
 * @param entry - One GraphQL error
 * @returns Its message texts, blanks removed
 */
export const messagesOf = (entry: GraphQLErrorLike): readonly string[] =>
  [
    textOf(entry.message),
    textOf(entry.extensions?.originalError?.message),
  ].filter(text => text.length > 0);

/**
 * Classifies a claim rejection without guessing at copy the backend did not
 * send.
 * @param error - The value the claim mutation rejected with
 * @returns The rejection kind and the copy to show
 */
export function classifyClaimOwnershipError(
  error: unknown
): ClaimOwnershipRejection {
  const entries = collectGraphQLErrors(error);

  if (isScenarioRevisionConflict(error)) {
    return { kind: "revision-conflict", message: REVISION_CONFLICT_MESSAGE };
  }
  if (
    entries.some(entry =>
      messagesOf(entry).some(text => text.includes(ALREADY_OWNED_MESSAGE))
    )
  ) {
    return { kind: "already-owned", message: ALREADY_CLAIMED_MESSAGE };
  }

  const forbidden = entries.find(
    entry => entry.extensions?.code === FORBIDDEN_CODE
  );
  if (forbidden !== undefined) {
    return {
      kind: "forbidden",
      message: messagesOf(forbidden)[0] ?? CLAIM_FAILED_MESSAGE,
    };
  }

  const fromEntries = entries.flatMap(messagesOf)[0];
  const topLevel = textOf((error as { message?: unknown } | null)?.message);
  return {
    kind: "other",
    message:
      fromEntries ?? (topLevel.length > 0 ? topLevel : CLAIM_FAILED_MESSAGE),
  };
}
